import type { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getAppEnv, viteFFOverrides } from '@/constants/env';
import * as FeatureFlagNames from '@/constants/featureFlags';
import { useFFLoadingState } from '@/context/featureFlags/FFLoadingContext';
import { Logger } from '@montugroup/utilities';

type UseLDFeatureFlagsType = {
  flags: LDFlagSet;
  loading: boolean;
};

const logger = new Logger('useFeatureFlags');
const FF_OVERRIDE_LOCAL_STORAGE_KEY = 'FEATURE_FLAG_OVERRIDES';

const ENV_WITH_FF_OVERRIDES = ['test', 'development', 'qa', 'staging'];
const shouldApplyFFOverrides = ENV_WITH_FF_OVERRIDES.includes(getAppEnv());

const getFFOverrides = (): { [key: string]: boolean } => {
  let overrides = {};
  const localStorageFFOverrides = localStorage.getItem(FF_OVERRIDE_LOCAL_STORAGE_KEY);
  const envFFOverrides = viteFFOverrides();

  if (localStorageFFOverrides || envFFOverrides) {
    try {
      const parsedLocalStorageFFOverrides = localStorageFFOverrides ? JSON.parse(localStorageFFOverrides) : {};

      overrides = {
        ...(envFFOverrides ?? {}),
        ...parsedLocalStorageFFOverrides,
      };

      const overriddenKeys = Object.entries(overrides).join(' | ');
      logger.info(`Overriding Feature Flags: ${overriddenKeys}`);
    } catch {
      overrides = {};
    }
  }

  return overrides;
};

sessionStorage.setItem('FEATURE_FLAG_KEYS', JSON.stringify(Object.values(FeatureFlagNames), null, 2));

/**
 * Main hook for accessing feature flags with loading state.
 *
 * **Key Behavior:**
 * - Returns bootstrap flag values immediately (loading=true)
 * - Transitions to actual LaunchDarkly values when ready (loading=false)
 * - Components should wait for loading=false before making critical decisions
 *
 * @returns Object containing flags and loading state
 */
export const useFeatureFlags = (): UseLDFeatureFlagsType => {
  const LDflags = useFlags();
  const { loading } = useFFLoadingState();

  const featureFlagOverrides = shouldApplyFFOverrides ? getFFOverrides() : {};

  const updatedFlags = {
    ...LDflags,
    ...featureFlagOverrides,
  };

  return { flags: updatedFlags, loading };
};

export default useFeatureFlags;
